// 구독 요금 판정 — 웹 복제본 (계획_요금변경_2026-10-04 §3·W1, 대표 승인 2026-10-04)
//
// ⚠️ 정본은 서버 functions/lib/billing-policy.js 의 decideBilling — 한쪽을 고치면 양쪽 함께.
//    이 파일은 "화면을 잠글지"만 정한다. 실제 청구 여부·금액은 서버가 다시 판정한다(화면 판정으로 돈이 나가지 않음).
//
// 핵심: 가입 당시 요금표(subscriptionTariffs/{판정 대상 uid — resolveBillingOwner})가 있으면 신규, 없으면 기존 회원.
//   - 치과(요금표 유무 무관)       = 청구 안 함(clinic_free — 2026-10-30 대표 결정, 모든 치과 무료)
//   - 기존 국내 기공소(요금표 없음) = LEGACY_LAB_FREE_UNTIL(2026-12-31 23:59:59 KST)까지 무료 — 데이터(trial 문서 만료일)가 아니라 코드 상수로 보장
//   - 요금표 있음                  = 체험 종료일(trialEndsAt)까지 무료, 이후 요금표 금액
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getFeatures, paywallActive } from './features';

// 요금표 — 서버 TARIFFS 와 같은 값(VAT 포함 청구액)
// 2026-10-30 구독 플랜제(계획_구독플랜제_2026-10-30 §1, 대표 결정): 기공소 플랜 3종(베이직·스탠다드·프리미엄) + 지그 월 한도.
//   옛 lab_33000_v6.2 는 features.planV8At 이후 베이직으로 본다(effectivePlanCode). 치과 요금표는 남아 있어도 청구하지 않는다(clinic_free).
export const TARIFFS = Object.freeze({
    'lab_33000_v6.2': Object.freeze({ code: 'lab_33000_v6.2', kind: 'lab', amount: 33000, label: '기공소 구독', jigLimit: 300 }),
    'clinic_99000_v6.2': Object.freeze({ code: 'clinic_99000_v6.2', kind: 'clinic', amount: 99000, label: '치과 구독', jigLimit: 0 }),
    'lab_basic_55000_v8': Object.freeze({ code: 'lab_basic_55000_v8', kind: 'lab', amount: 55000, label: '베이직', jigLimit: 300 }),
    'lab_standard_110000_v8': Object.freeze({ code: 'lab_standard_110000_v8', kind: 'lab', amount: 110000, label: '스탠다드', jigLimit: 600 }),
    'lab_premium_220000_v8': Object.freeze({ code: 'lab_premium_220000_v8', kind: 'lab', amount: 220000, label: '프리미엄', jigLimit: 1200 }),
});
// 기공소 플랜 순서(낮은 순) — 플랜 카드·표·"다음 플랜" 판정에 쓴다. 서버 LAB_PLANS 와 같은 순서.
export const LAB_PLANS = Object.freeze([
    TARIFFS['lab_basic_55000_v8'],
    TARIFFS['lab_standard_110000_v8'],
    TARIFFS['lab_premium_220000_v8'],
]);
export const LAB_PLAN_CODES = Object.freeze(LAB_PLANS.map(p => p.code));
export const DEFAULT_LAB_PLAN = LAB_PLANS[0];   // 신규 기공소 기본·체험 중 한도 = 베이직
export const LEGACY_LAB = Object.freeze({ code: 'lab_33000_legacy', kind: 'lab', amount: 33000, label: '기존 기공소', jigLimit: 300 });

/** features.planV8At(ISO 문자열·ms·Timestamp)이 지났는가 — 대표가 날짜를 넣기 전(없음)에는 false. 서버와 같은 규칙 */
export function planV8Active(features, now = Date.now()) {
    const v = features?.planV8At;
    if (!v) return false;
    const d = typeof v?.toDate === 'function' ? v.toDate()
        : (typeof v?.seconds === 'number' ? new Date(v.seconds * 1000) : new Date(v));
    const ms = d.getTime();
    return Number.isFinite(ms) && now >= ms;
}

/**
 * 실제로 적용되는 플랜 코드 — 옛 33,000원 요금표(lab_33000_v6.2·lab_33000_legacy)는 planV8At 이후 베이직. 그 전엔 그대로.
 * 치과·알 수 없는 코드는 그대로 돌려준다.
 */
export function effectivePlanCode(code, features, now = Date.now()) {
    if (!code) return code || null;
    if ((code === 'lab_33000_v6.2' || code === LEGACY_LAB.code) && planV8Active(features, now)) return DEFAULT_LAB_PLAN.code;
    return code;
}

/** 코드 → 플랜(요금표) 객체. 옛 코드는 effectivePlanCode 를 거친 뒤 찾는다. 없으면 null */
export function planForCode(code, features, now = Date.now()) {
    const c = effectivePlanCode(code, features, now);
    if (!c) return null;
    if (c === LEGACY_LAB.code) return LEGACY_LAB;
    return TARIFFS[c] || null;
}

/** 다음(한 단계 위) 기공소 플랜. 프리미엄·플랜 아님이면 null */
export function nextLabPlan(code) {
    const i = LAB_PLAN_CODES.indexOf(code);
    return i >= 0 && i < LAB_PLANS.length - 1 ? LAB_PLANS[i + 1] : null;
}
// 기존 기공소 무료 약속(2026-08-06 대표 확정) — 서버 LEGACY_LAB_FREE_UNTIL 과 같은 시각
export const LEGACY_LAB_FREE_UNTIL = new Date('2026-12-31T23:59:59+09:00');

const CLINIC_TYPES = ['dental', 'clinic', '치과'];

// 결제 실패 유예 일수 — 서버와 같은 이름·의미. 7일 = 대표 결정(2026-10-04, 검토 P1-1).
// 의미: 자동결제가 실패한 active 구독을 만료(expiresAt) 후 이 일수만큼 더 '살아 있는 구독'으로 본다.
//   해지(cancelled)·체험(trial)·past_due 에는 적용하지 않는다(해지는 낸 기간까지만, 체험은 종료일까지만,
//   환불 종료 구독은 cancelled 라 자동 제외).
// ⚠️ 서버 functions/lib/billing-policy.js 의 BILLING_GRACE_DAYS(=7)와 같은 값이어야 한다 — 한쪽을 고치면 양쪽 함께.
//    유예 기산은 결제일(=expiresAt 다음 날) 기준. past_due 로 넘어간 구독에는 유예가 적용되지 않는다.
export const BILLING_GRACE_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

// 구독 판정용 관리자 — 서버 billing-policy.js isAdminUser 와 같은 기준(users 문서 isAdmin/role).
// 화면 메뉴용 utils/adminClaim.isAdminUser(커스텀 클레임 포함)와 일부러 다르다: 요금 판정은 서버 견적과 같아야
// 화면(무료)·서버(청구) 불일치가 생기지 않는다. 클레임만 있는 관리자는 서버도 '관리자 면제'로 보지 않는다.
function isBillingAdmin(u) {
    return !!u && (u.isAdmin === true || u.role === 'admin');
}

export function isClinicType(bt) {
    return CLINIC_TYPES.includes(bt || '');
}

const toDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof v.toDate === 'function') return v.toDate();
    if (typeof v === 'number' || typeof v === 'string') {
        const d = new Date(v);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);
    return null;
};

/**
 * §3 판정표 (서버 decideBilling 과 같은 순서). owner = 대표(소유) 계정 users 문서, tariff = 요금표 문서 | null
 * @returns {{billable:boolean, reason:string, amount:number, tariffCode:string|null, freeUntil:Date|null, legacy:boolean, exempt:boolean, kind:string|null}}
 */
export function decideBilling({ features, owner, tariff }) {
    const base = { billable: false, amount: 0, tariffCode: null, freeUntil: null, legacy: false, exempt: false, kind: null };
    const f = features || {};
    const u = owner || {};
    // 1. 요금 잠금 미적용(allFree=true) — W2: paywallActive 한 곳으로 판정 (서버 decideBilling 과 같은 이름·결과, reason 은 종전 'all_free' 유지)
    if (!paywallActive(f)) return { ...base, reason: 'all_free' };
    // 2. 구독 대상 아님 — 관리자
    if (isBillingAdmin(u)) return { ...base, reason: 'admin' };
    // 3. 면제 — 치과·기공소 공통 (users.subscriptionExempt — 관리자·스크립트 전용 필드)
    if (u.subscriptionExempt === true) return { ...base, reason: 'exempt', exempt: true };
    // 6. 요금표 있음(신규) — 요금표가 가입 당시 유형을 고정
    if (tariff && tariff.code) {
        const t = TARIFFS[tariff.code];
        if (!t) return { ...base, reason: 'tariff_unknown', tariffCode: tariff.code };
        // 2026-10-30 대표 결정: 치과는 요금표가 있어도 청구하지 않는다(무료) — 잠금 모드 없음
        if (t.kind === 'clinic') return { ...base, reason: 'clinic_free', kind: 'clinic', tariffCode: t.code };
        // 옛 33,000원 요금표는 planV8At 이후 베이직(55,000원)으로 — 금액·코드 모두 유효 플랜 기준
        const eff = planForCode(t.code, f) || t;
        return { ...base, billable: true, reason: 'tariff', kind: eff.kind, amount: eff.amount, tariffCode: eff.code, freeUntil: toDate(tariff.trialEndsAt) };
    }
    if (u.labKind === 'overseas') return { ...base, reason: 'overseas' };
    // 4. 기존 치과 — 무료 (2026-10-30: 종전 legacy_clinic → clinic_free 로 통일. 모든 치과 무료)
    if (isClinicType(u.businessType)) return { ...base, reason: 'clinic_free', legacy: true, kind: 'clinic' };
    // 5. 기존 국내 기공소 — 12-31까지 무료, 이후 33,000원(planV8At 이후엔 베이직 55,000원)
    if (u.businessType === 'lab') {
        const eff = planForCode(LEGACY_LAB.code, f) || LEGACY_LAB;
        return { ...base, billable: true, reason: 'legacy_lab', legacy: true, kind: 'lab', amount: eff.amount, tariffCode: eff.code, freeUntil: LEGACY_LAB_FREE_UNTIL };
    }
    // 그 외 유형(outsource·seller·individual 등) — 구독 대상 아님
    return { ...base, reason: 'not_subject' };
}

/** 지금 무료로 이용 가능한가 — 청구 대상이 아니거나 무료기간(체험·12-31) 안 */
export function isFreeNow(decision, now = new Date()) {
    if (!decision || !decision.billable) return true;
    return !!decision.freeUntil && decision.freeUntil.getTime() > now.getTime();
}

/**
 * 이용권이 살아 있는 구독 문서인가 — 서버 isLiveSub 과 같은 규칙(cancelled 라도 expiresAt 이 남았으면 유효)
 * + BILLING_GRACE_DAYS(active 만). expiresAt 없는 고아 문서는 무효.
 */
export function isLiveSub(sub, now = new Date()) {
    if (!sub) return false;
    if (!['active', 'trial', 'cancelled'].includes(sub.status)) return false;
    const exp = toDate(sub.expiresAt);
    if (!exp) return false;
    const grace = sub.status === 'active' ? BILLING_GRACE_DAYS * DAY_MS : 0;
    return exp.getTime() + grace > now.getTime();
}

/**
 * 결제 유예 중인가 — active 구독이 만료(expiresAt)를 지났지만 유예(BILLING_GRACE_DAYS) 안에 있음.
 * 구독 화면의 "결제가 실패했습니다 — ○월 ○일까지 카드를 확인해 주세요" 안내용. isLiveSub 와 같은 기준.
 * @returns {{inGrace:boolean, graceUntil:Date|null}}
 */
export function getGraceInfo(sub, now = new Date()) {
    const none = { inGrace: false, graceUntil: null };
    if (!sub || sub.status !== 'active' || BILLING_GRACE_DAYS <= 0) return none;
    const exp = toDate(sub.expiresAt);
    if (!exp || exp.getTime() > now.getTime()) return none;
    const graceUntil = new Date(exp.getTime() + BILLING_GRACE_DAYS * DAY_MS);
    return graceUntil.getTime() > now.getTime() ? { inGrace: true, graceUntil } : none;
}

/** 요금표가 있는 회원(신규)으로 판정됐는가 — 요금표가 유형을 고정하므로 해외(labKind) 단축 통과를 무시해야 한다 */
export function decisionHasTariff(decision) {
    return decision?.reason === 'tariff' || decision?.reason === 'tariff_unknown';
}

/** 요금표 읽기. 없으면 null(기존 회원). 읽기 실패는 throw — 호출자가 안전측을 정한다 */
export async function loadTariff(ownerUid) {
    const snap = await getDoc(doc(db, 'subscriptionTariffs', ownerUid));
    return snap.exists() ? snap.data() : null;
}

/**
 * 판정 대상(소유) 계정 결정 — 서버 billing-policy.js resolveOwner 와 같은 규칙 (보안 P1-1, 2026-10-04).
 *   - 본인 요금표(subscriptionTariffs/{본인 uid})가 있으면 companyId 가 있어도 본인 uid·본인 구독·본인 요금표로 판정
 *     (가입 당시 업체 대표였던 계정이 다른 업체 직원으로 들어가도 소속 업체 구독으로 빠지지 않게)
 *   - 없으면 종전대로 직원은 소속 업체(companyId), 대표는 본인
 * 본인 요금표 읽기 실패는 기록하고 종전 규칙(소속 업체)으로 판정한다 — 서버가 다시 판정하므로 화면 판정으로 청구되지 않는다.
 * @param {object} user  { uid, companyId }
 * @returns {Promise<{ownerUid:string|null, isStaff:boolean, selfTariff:boolean, tariff:(object|null|undefined)}>}
 *   tariff: 본인 요금표로 판정할 때 그 문서. 소속 업체 기준이면 undefined(아직 안 읽음)
 */
export async function resolveBillingOwner(user) {
    const uid = user?.uid || null;
    if (!uid) return { ownerUid: null, isStaff: false, selfTariff: false, tariff: undefined };
    if (!user.companyId || user.companyId === uid) {
        return { ownerUid: uid, isStaff: false, selfTariff: false, tariff: undefined };
    }
    try {
        const own = await loadTariff(uid);
        if (own && own.code) return { ownerUid: uid, isStaff: false, selfTariff: true, tariff: own };
    } catch (e) {
        console.error('[billingPolicy] 본인 요금표 읽기 실패 — 소속 업체 기준으로 판정:', e?.message);
    }
    return { ownerUid: user.companyId, isStaff: true, selfTariff: false, tariff: undefined };
}

/**
 * 로그인 사용자(대표·직원) 기준 판정. 판정 대상은 resolveBillingOwner 규칙(본인 요금표 우선 → 없으면 소속 업체).
 * 읽기 실패는 { reason: '..._read_failed', billable:false } — 서버도 읽기 실패 시 청구하지 않으므로 화면도 잠그지 않는다(기록은 남김).
 * @param {object} user  App.js userData (uid, companyId, businessType, companyBusinessType, subscriptionExempt, labKind ...)
 * @param {object} [opt] { features, tariff, resolved }
 *   resolved: 이미 구한 resolveBillingOwner 결과(있으면 다시 읽지 않음)
 *   tariff: 판정 대상(resolved.ownerUid) 요금표 — undefined=읽기, null=요금표 없음
 */
export async function loadBillingDecision(user, opt = {}) {
    const fail = (reason) => ({ billable: false, reason, amount: 0, tariffCode: null, freeUntil: null, legacy: false, exempt: false, kind: null, ownerUid: null });
    if (!user?.uid) return fail('no_user');
    const resolved = opt.resolved || await resolveBillingOwner(user);
    const ownerUid = resolved.ownerUid;
    const features = opt.features || await getFeatures();
    // 설정 읽기 실패 = 청구 판정 보류(열림) — 서버 resolveBilling 의 features_read_failed 와 같은 안전측 (기록은 getFeatures 가 남김)
    if (features?._readFailed === true) return { ...fail('features_read_failed'), ownerUid };
    let owner = user;
    if (resolved.isStaff) {
        try {
            const os = await getDoc(doc(db, 'users', user.companyId));
            owner = os.exists() ? { ...os.data(), uid: user.companyId } : null;
        } catch (e) {
            console.error('[billingPolicy] 소속 업체 문서 읽기 실패 — 잠그지 않음:', e?.message);
            return { ...fail('owner_read_failed'), ownerUid };
        }
        if (!owner) {
            // 소속 업체 문서가 없으면 직원 문서의 업체 유형으로만 판정 (면제·해외 정보 없음)
            owner = { uid: user.companyId, businessType: user.companyBusinessType };
        }
    }
    let tariff = null;
    try {
        if (opt.tariff !== undefined) tariff = opt.tariff;
        else if (resolved.tariff !== undefined) tariff = resolved.tariff;
        else tariff = await loadTariff(ownerUid);
    } catch (e) {
        console.error('[billingPolicy] 요금표 읽기 실패 — 잠그지 않음:', e?.message);
        return { ...fail('tariff_read_failed'), ownerUid };
    }
    return { ...decideBilling({ features, owner, tariff }), ownerUid };
}

// ─────────────────────────────────────────────────────────────────────────────
// 접근 모드 (계획_가입승인결제흐름_2026-10-15 §3-3, 대표 승인 10-15 · W2)
//   서버 billing-policy.js(decideWebAccess 반환 accessMode)·앱 billing_policy.dart 와 같은 이름·같은 허용 범위.
//   ⚠️ 한쪽을 고치면 세 곳 함께. 이 판정은 "화면을 잠글지"만 정한다(청구는 서버가 다시 판정).
//
//   pending           = 가입 승인 대기·거절 (App.js 승인 대기 화면이 담당 — 약관·처리방침·로그아웃만)
//   full              = 관리자·면제·베타·해외·구독 대상 아님·무료 기간·살아 있는 구독·allFree=true(paywallActive false)·판정 실패(fail-open)
//   lab_limited       = 국내 기공소(대표·직원) && 그 밖 — 받은 의뢰서(상세·인쇄·첨부) + 계정·홍보·거래처·치과 초대 + 기존 명세서·계산서 읽기 전용(법무 R4)
//   clinic_v62_locked = 요금표 policy 가 v7.1 이 아닌 치과 && 그 밖 — 현행 v6.2 범위(ClinicSubGate 9곳 중 「받은 명세서」는 잠금 대신 읽기 전용 — 대표 10-15 "응"). 의뢰서 작성·보낸 의뢰서는 무료
//   clinic_locked     = 요금표 policy 'v7.1' 치과 && 그 밖 — 결제·법정·설정·환자 명부 + 의뢰서 작성·보낸 의뢰서(대표 10-15 추가 결정: 모든 치과 무료)
//                       (1단계에서는 v7.1 요금표가 없어 이 모드는 나오지 않는다 — 2단계 termsV71At 이후)
//   대표 10-15 추가 결정: "의뢰서 작성과 의뢰서 목록(받은 의뢰서)은 무료로 열어 두자" — 초대 예외 판정(clinicInviteGrants)은 만들지 않음.
// ─────────────────────────────────────────────────────────────────────────────
export const ACCESS_MODES = Object.freeze({
    PENDING: 'pending',
    FULL: 'full',
    LAB_LIMITED: 'lab_limited',
    CLINIC_V62_LOCKED: 'clinic_v62_locked',
    CLINIC_LOCKED: 'clinic_locked',
});

// 모든 잠금 모드에서 공통으로 열려 있어야 하는 경로 — 결제·결제 내역·계정 삭제·약관·처리방침 (완료 기준 6-1)
const ALWAYS_OPEN = Object.freeze(['/subscription', '/billing', '/account/delete', '/terms', '/privacy', '/location-terms']);
// 의뢰서 경로 — 받은/보낸 의뢰서 목록·상세(인쇄·첨부 내려받기는 상세 화면 안). 대표 10-15: 결제 없이 열어 둠
const ORDER_PATHS = Object.freeze(['/orders', '/view-order', '/view-message']);

/**
 * 허용 목록 — 한 곳에만 둔다(계획 §3-3). 매칭 규칙(isPathAllowedForMode):
 *   all:true    → 전부 열림(잠금은 화면별 게이트가 담당)
 *   deny        → 먼저 검사. 같은 경로이거나 그 하위면 잠김
 *   prefix      → 같은 경로이거나 '/' 로 이어지는 하위 경로면 열림 ('/orders' 는 '/orders/x' 를 열지만 '/ordersX' 는 열지 않음)
 *   exact       → 정확히 같은 경로만 열림
 */
export const ACCESS_ALLOW = Object.freeze({
    pending: Object.freeze({
        all: false,
        prefix: Object.freeze(['/terms', '/privacy', '/location-terms']),
        exact: Object.freeze([]),
        deny: Object.freeze([]),
    }),
    full: Object.freeze({ all: true, prefix: Object.freeze([]), exact: Object.freeze([]), deny: Object.freeze([]) }),
    lab_limited: Object.freeze({
        all: false,
        prefix: Object.freeze([
            ...ORDER_PATHS,             // 받은 의뢰서 목록·상세·인쇄·첨부 ([CAD로 보내기]는 "구독 후 이용" — OrderList)
            '/profile',                 // 계정 정보
            '/lab/promo-settings',      // 홍보 프로필 (App 최상위 라우트라 MainLayout 가드 밖이지만 표에 명시)
            '/partners',                // 거래처
            '/connections',
            '/clinic-invites',          // 치과 초대
            '/transactions',            // 기존 거래명세서 — 읽기 전용(작성·발행·수정·삭제 숨김, 목록·상세·PDF·인쇄 유지) — 법무 R4
            '/tax-invoices',            // 기존 세금계산서 목록·상세 — 읽기 전용. 작성(/tax-invoices/create)은 deny
            '/staff-invitations',
            '/staff-affiliation',
            '/staff-join',
            '/security-lock',           // 잠금(PIN) 설정 — 서류 화면 SensitiveGate 와 짝
            '/outsource-request',       // 외주 의뢰서 — 구독 없이 열림(대표 10-30 "구독을 안해도 오픈"). 보내기·받기·연결·월별 요약 전부
            '/settings/outsource-vendor',   // 외주 수주 설정(업체 등록) — 외주 의뢰서 머리의 바로가기
            '/download-agent',          // 덴클 프로그램 받기 — 미구독도 PC 외주 보내기(외주 전용 모드, 에이전트 alpha.117)
            ...ALWAYS_OPEN,
        ]),
        exact: Object.freeze(['/settings']),   // 설정 첫 화면만 (하위 카드 경로는 잠김)
        deny: Object.freeze(['/tax-invoices/create']),
    }),
    clinic_v62_locked: Object.freeze({ all: true, prefix: Object.freeze([]), exact: Object.freeze([]), deny: Object.freeze([]) }),
    clinic_locked: Object.freeze({
        all: false,
        prefix: Object.freeze([
            '/create-order',            // 의뢰서 작성 — 모든 치과 무료(대표 10-15)
            ...ORDER_PATHS,             // 보낸 의뢰서 목록·상세
            '/profile',
            '/transactions',            // 받은 명세서 — 읽기 전용(목록·상세·인쇄·PDF·엑셀, 정산 확인·수정 요청 숨김) — 대표 10-15 "응", 법무 R4
            '/patients',                // 환자 명부 열람·삭제 (읽기 전용 화면 전환은 2단계 W17)
            '/settings/clinic-delegation',   // 환자 정보 처리위탁
            '/staff-invitations',
            '/staff-affiliation',
            '/staff-join',
            '/security-lock',
            ...ALWAYS_OPEN,
        ]),
        exact: Object.freeze(['/settings']),
        deny: Object.freeze([]),
    }),
});

const matchPrefix = (path, p) => path === p || path.startsWith(p.endsWith('/') ? p : p + '/');

/** 그 모드에서 이 경로가 열려 있는가. 모르는 모드는 열림(fail-open — 판정 실패로 잠그지 않음) */
export function isPathAllowedForMode(mode, pathname) {
    const rule = ACCESS_ALLOW[mode];
    if (!rule) return true;
    const path = (pathname || '/').replace(/\/+$/, '') || '/';
    if (rule.deny.some(p => matchPrefix(path, p))) return false;
    if (rule.all) return true;
    if (rule.exact.includes(path)) return true;
    return rule.prefix.some(p => matchPrefix(path, p));
}

/** 요금표가 v7.1 규칙(체험 0일·치과 잠금 모드)인가 — 서버 stampTariff 의 policy 기록(2단계 S9)을 따른다 */
export function isV71Tariff(tariff) {
    if (!tariff) return false;
    if (tariff.policy === 'v7.1') return true;
    return typeof tariff.code === 'string' && tariff.code.endsWith('_v7.1');
}

/** 국내 기공소(대표·직원)인가 — MainLayout 미구독 가드와 같은 기준(businessType 없는 옛 기공소는 userType 'lab') */
export function isDomesticLabAccount(user) {
    const u = user || {};
    if (u.labKind === 'overseas') return false;
    const bt = u.businessType || u.companyBusinessType;
    return bt === 'lab' || (!bt && u.userType === 'lab');
}

/**
 * 접근 모드 판정 — 우선순위 pending → full 조건 → 모드별 (계획 §3-3).
 * @param {object} p
 *   user        App.js userData (approvalStatus·labKind·hasSubscriptionTariff·businessType·companyBusinessType·userType)
 *   features    config/features (paywallActive 판정)
 *   isSubscribed App.js 가 구한 "열림" 판정 — 살아 있는 구독·무료 기간·면제·기존 회원·베타·관리자·판정 실패(fail-open) 를 이미 포함
 *   tariff      판정 대상(소유) 계정 요금표 | null | undefined(못 읽음)
 *   isAdmin     관리자 여부(화면 메뉴 기준 — 선택)
 * @returns {'pending'|'full'|'lab_limited'|'clinic_v62_locked'|'clinic_locked'}
 */
export function computeAccessMode({ user, features, isSubscribed, tariff, isAdmin } = {}) {
    const u = user || {};
    // pending — App.js 승인 대기 게이트와 같은 조건 (해외 기공소는 /global/status 가 담당, 단 요금표가 있으면 해외 제외 무시 — 보안 P1-1)
    const overseasBypass = u.labKind === 'overseas' && u.hasSubscriptionTariff !== true;
    if (!overseasBypass && (u.approvalStatus === 'pending' || u.approvalStatus === 'rejected')) return ACCESS_MODES.PENDING;
    if (!paywallActive(features)) return ACCESS_MODES.FULL;
    if (isAdmin === true || isBillingAdmin(u)) return ACCESS_MODES.FULL;
    if (isSubscribed === true) return ACCESS_MODES.FULL;
    const bt = u.businessType || u.companyBusinessType;
    // 2026-10-30 대표 결정: 치과는 무료(clinic_free) — 잠금 모드 없이 full. (clinic_v62_locked·clinic_locked 상수는 다른 화면 참조 때문에 남겨 둔다)
    if (isClinicType(bt)) return ACCESS_MODES.FULL;
    if (isDomesticLabAccount(u)) return ACCESS_MODES.LAB_LIMITED;
    // 그 외 유형(해외·밀링센터·판매자·개인 등) — 구독 대상 아님
    return ACCESS_MODES.FULL;
}
